import {
    PACKAGES_PER_PAGE_OPTIONS,
    PACKAGE_TYPES,
    PACKAGE_SORT_OPTIONS,
    PACKAGE_QUERY_OPTIONS,
    PACKAGE_DIR_OPTIONS,
    VISIBILITY_ACCOUNT,
    VISIBILITY_SENDER,
    PACKAGE_PREDEFINED_OPTIONS,
    LAST_TRANSACTION_COUNT
} from '../../constants';

type PaginationOptions = {
    isFrom: boolean;
    values: string[];
}

type PackageParams = {
    query?: string;
    type?: string;
    from?: number;
    to?: number;
    sort?: string;
    dir?: string;
    visibility?: string;
    predefined?: string;
    updatedSinceDays?: number;
    search?: string;
}

type SharedTemplatesPreference = {
    share_templates?: boolean;
}

// Helper function to check if a number is valid for pagination
const isValidPaginationNumber = (number: number, options: PaginationOptions): boolean => {
    if (!number || Number.isNaN(number)) return false;

    // For 'from' parameter, it should be 1 or start of a page
    if (options.isFrom) {
        return number === 1 || options.values.some((pageSize) => (number - 1) % Number(pageSize) === 0);
    }

    // For 'to' parameter, it should be a multiple of page size
    return options.values.some((pageSize) => number % Number(pageSize) === 0);
};

function isQueryValid(query?: string): boolean {
    return Boolean(query && Object.values(PACKAGE_QUERY_OPTIONS).includes(query));
}

function isTypeValid(type?: string): boolean {
    return Boolean(type && Object.values(PACKAGE_TYPES).includes(type));
}

function isFromValid(from?: number): boolean {
    return isValidPaginationNumber(from || 0, {
        isFrom: true,
        values: Object.values(PACKAGES_PER_PAGE_OPTIONS)
    });
}

function isToValid(to?: number): boolean {
    if (!to || Number.isNaN(to) || to < 1) return false;

    if (to === LAST_TRANSACTION_COUNT) return true;

    const pageSizes = Object.values(PACKAGES_PER_PAGE_OPTIONS).map(Number);
    const smallestPageSize = Math.min(...pageSizes);

    return pageSizes.some((pageSize) => to % pageSize === 0) || to <= smallestPageSize;
}

function isSortValid(sort?: string): boolean {
    return Boolean(sort && Object.values(PACKAGE_SORT_OPTIONS).includes(sort));
}

function isDirValid(dir?: string): boolean {
    return Boolean(dir && Object.values(PACKAGE_DIR_OPTIONS).includes(dir));
}

function isVisibilityValid(visibility?: string): boolean {
    return Boolean(visibility && (visibility === VISIBILITY_ACCOUNT || visibility === VISIBILITY_SENDER));
}

function isPredefinedValid(predefined?: string): boolean {
    return Boolean(predefined && Object.values(PACKAGE_PREDEFINED_OPTIONS).includes(predefined));
}

function getValidPackagesParams(params: PackageParams = {}, userAttributes?: SharedTemplatesPreference): PackageParams {
    const validParams: PackageParams = {};

    if (isQueryValid(params.query)) {
        validParams.query = params.query;
    }

    if (isTypeValid(params.type)) {
        validParams.type = params.type;
    }

    if (isFromValid(params.from)) {
        validParams.from = params.from;
    }

    if (isToValid(params.to)) {
        validParams.to = params.to;
    }

    if (isSortValid(params.sort)) {
        validParams.sort = params.sort;
    }

    if (isDirValid(params.dir)) {
        validParams.dir = params.dir;
    }

    if (isVisibilityValid(params.visibility)) {
        validParams.visibility = params.visibility;
    } else if (userAttributes) {
        validParams.visibility = userAttributes.share_templates ? VISIBILITY_ACCOUNT : VISIBILITY_SENDER;
    }

    if (isPredefinedValid(params.predefined)) {
        validParams.predefined = params.predefined;
    }

    validParams.updatedSinceDays = params.updatedSinceDays;
    validParams.search = params.search;

    return validParams;
}

export {getValidPackagesParams};
